import { EMPTY } from "../../../../common/constants/swiftConstants";
import { AggregationOperation, AggregationSubOperation, InventoryMonitoringAggregationCriteria, SearchType } from "../../../../interfaces/consumptionPreferenceInterfaces";
import { BURN_DATE, DefaultSnapshotMonitoringData, LONG, RANGE } from "../config/ConsumptionCPLConfig";

export const getDate = (date: any): string => {
  if (!date && date === "") return "";
  date = parseInt(date);
  date = date > 1000000000000 ? date : date * 1000;

  const year = new Date(date).getUTCFullYear();
  const month = String(new Date(date).getUTCMonth() + 1).padStart(2, "0");
  const day = String(new Date(date).getUTCDate()).padStart(2, "0");
  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate.toString();
};

export const convertToEpoch = (dateString) => {
  const [day, month, year] = dateString
    .split("/")
    .map((part) => parseInt(part, 10));
  const date = new Date(year, month - 1, day);
  return date.getTime();
};

export const getSfgNameAndSfgId = (id) => {
  const splittedData = id.split('_');
  const sfgId = splittedData[0];
  let sfgName = "";

  if(splittedData.length > 2) {
    sfgName = splittedData.slice(1).join("_")
  } else {
    sfgName = splittedData[1];
  }

  return [sfgId, sfgName];
};

export const getDefaultLevelsPieData = () => {
  return {convertedData: [], total: "", othersData: []};
}

export const getDefaultLevelsBarData = () => {
  return { convertedData: [], maxData: {} };
};

export const getDefaultTrendData = () => {
  return {convertedData:[], maxData: {}};
}

export function unitConversion(e, isDecimalNeeded = false) {
  try {
      return Math.abs(e) >= 1e9
          ? (e / 1e9).toFixed(2).replace(/\.0$/, "") +
          "B"
          : Math.abs(e) >= 1e6
              ? (e / 1e6).toFixed(2).replace(/\.0$/, "") +
              "M"
              : Math.abs(e) >= 1e3
                  ? (e / 1e3).toFixed(2).replace(/\.0$/, "") +
                  "K"
                  : isDecimalNeeded ? parseFloat(e).toFixed(2) : parseFloat(e).toFixed(0);
  } catch(err){
      console.log(err);
  }
}

export const convertLevelsBarDataFormat = (response) => {
    let maxData = {};
    let ymax = 0, ymin = 100000000000;

    let convertedData = response.views.map(component => {
        let dataSet = {
            title: component.key,
            type: "bar",
            data: [{x : new Date(), y: parseInt(component.value)}],
            valueFormatter: unitConversion
        };
        ymax = Math.max(ymax, parseInt(component.value));
        ymin = Math.min(ymin, parseInt(component.value));
        return dataSet;
    });
    maxData = {"yMax": ymax, "yMin":ymin };
    return {convertedData, maxData};
}

export const convertDeviationPercentageBarDataFormat = (response) => {
  let maxData = {};
  let ymax = 0, ymin = 100000000000;

  let convertedData = response.views
      .filter(component => {
          return Math.abs(parseFloat(component.value)) > 0.000001;
      })
      .map(component => {
          let value = parseFloat(component.value);
          let dataSet = {
              title: component.key,
              type: "bar",
              data: [{x: new Date(), y: value}],
              valueFormatter: unitConversion
          };
          ymax = Math.max(ymax, value);
          ymin = Math.min(ymin, value);
          return dataSet;
      });
  
  maxData = {"yMax": ymax, "yMin": convertedData.length > 0 ? ymin : 0};
  return {convertedData, maxData};
};

export function convertTrendsDataFormat(responseArray) {
  let ymax = 0;
  let ymin = Infinity;
  const seriesMap = {};

  const percentageFormatter = (value) => `${parseFloat(unitConversion(value)).toFixed(0)}%`;
  responseArray?.forEach((trendObj) => {
    trendObj.views.forEach(view => {
      const seriesName = view.name.includes('.') ? view.name.split('.')[1] : view.name;
      view.values.forEach(val => {
        const timestamp = Number(val.x);
        let xValue;
        if (timestamp === 0) {
          const now = new Date();
          xValue = new Date(now.getFullYear(), now.getMonth(), 1);
        } else {
          xValue = new Date(timestamp);
        }
        const yValue = Number(val.y);
        if (!seriesMap[seriesName]) {
          seriesMap[seriesName] = {
            title: seriesName,
            type: "line",
            data: [],
            valueFormatter: percentageFormatter
          };
        }
        seriesMap[seriesName].data.push({ x: xValue, y: yValue });
        ymax = Math.max(ymax, yValue);
        ymin = Math.min(ymin, yValue);
      });
    });
  });

  Object.values(seriesMap).forEach(series => {
    series.data.sort((a, b) => a.x - b.x);
  });

  const convertedData = Object.values(seriesMap);
  const maxData = { yMax: ymax, yMin: ymin };
  return { convertedData, maxData };
}

export function convertConfiguredAndActualTAMTrendsDataFormat(responseArray) {
  let ymax = 0;
  let ymin = Infinity;
  const seriesMap = {};

  const unitConversion = (value) => `${value}%`;
  responseArray?.forEach((trendObj) => {
    if (trendObj.type === "CONFIGURED_AND_ACTUAL_TAM") {
      trendObj.views.forEach((view) => {
        const parts = view.name.split(".");
        const lgName = parts.length > 1 ? parts[1] : parts[0];

        const configuredKey = `${lgName} - Configured TAM`;
        const actualKey = `${lgName} - Actual TAM`;

        if (!seriesMap[configuredKey]) {
          seriesMap[configuredKey] = {
            title: configuredKey,
            type: "line",
            data: [],
            valueFormatter: unitConversion,
          };
        }
        if (!seriesMap[actualKey]) {
          seriesMap[actualKey] = {
            title: actualKey,
            type: "line",
            data: [],
            valueFormatter: unitConversion,
          };
        }

        if (view.values && view.values.length >= 2) {
          const configuredPoint = view.values[0];
          const actualPoint = view.values[1];

          const configuredTimestamp = Number(configuredPoint.x);
          const now = new Date();
          const configuredX = configuredTimestamp === 0 
            ? new Date(now.getFullYear(), now.getMonth(), 1) 
            : new Date(configuredTimestamp);
          const configuredY = Number(configuredPoint.y);

          const actualTimestamp = Number(actualPoint.x);
          const actualX = actualTimestamp === 0 
            ? new Date(now.getFullYear(), now.getMonth(), 1) 
            : new Date(actualTimestamp);
          const actualY = Number(actualPoint.y);

          seriesMap[configuredKey].data.push({ x: configuredX, y: configuredY });
          seriesMap[actualKey].data.push({ x: actualX, y: actualY });

          ymax = Math.max(ymax, configuredY, actualY);
          ymin = Math.min(ymin, configuredY, actualY);
        }
      });
    }
  });

  Object.values(seriesMap).forEach((series) => {
    series.data.sort((a, b) => a.x - b.x);
  });

  const convertedData = Object.values(seriesMap);
  const maxData = { yMax: ymax, yMin: ymin };
  return { convertedData, maxData };
}

export function getChartDomainAndTickFormatter(convertedData) {
  let allPoints = [];
  (convertedData || []).forEach(series => {
    allPoints = allPoints.concat(series.data);
  });

  let xDomain;
  let minTime, maxTime;
  if (allPoints.length > 0) {
    allPoints.sort((a, b) => a.x - b.x);
    minTime = allPoints[0].x.getTime();
    maxTime = allPoints[allPoints.length - 1].x.getTime();
    const diff = maxTime - minTime;
    xDomain = [new Date(minTime - diff * 0.05), new Date(maxTime + diff * 0.05)];
  }
  const tolerance = 24 * 60 * 60 * 1000;
  const xTickFormatter = (value) => {
    if (minTime && Math.abs(value - minTime) < tolerance) {
      return new Date(minTime).toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
    if (maxTime && Math.abs(value - maxTime) < tolerance) {
      return new Date(maxTime).toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
    return new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };
  return { xDomain, xTickFormatter };
}

export function yTickFormatter(value) {
  let formattedValue;
  if (Math.abs(value) >= 1e9) {
    formattedValue = (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (Math.abs(value) >= 1e6) {
    formattedValue = (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (Math.abs(value) >= 1e3) {
    formattedValue = (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    formattedValue = value >= 1 || value === 0 ? value.toFixed(0) : value.toFixed(3);
  }
  return `${formattedValue}%`;
}

export const convertConfiguredData = (response) => {
    let maxData = {};
    let ymax = 0, ymin = 100000000000;

    let convertedData = response.views.map(component => {
      let configuredTamValue = 0;
      let actualTamValue = 0;
  
      if (component.subAttribute && Array.isArray(component.subAttribute)) {
        const configuredTam = component.subAttribute.find(attr => attr.key === "CONFIGURED_TAM");
        const actualTam = component.subAttribute.find(attr => attr.key === "ACTUAL_TAM");
  
        if (configuredTam && configuredTam.value) {
          configuredTamValue = parseFloat(configuredTam.value);
        }
  
        if (actualTam && actualTam.value) {
          actualTamValue = parseFloat(actualTam.value);
        }
      }
  
      if (isNaN(configuredTamValue)) configuredTamValue = 0;
      if (isNaN(actualTamValue)) actualTamValue = 0;
  
      return [
        {
          title: `${component.key} - Configured TAM`,
          type: "bar",
          data: [{ x: component.key, y: configuredTamValue }],
          valueFormatter: unitConversion
        },
        {
          title: `${component.key} - Current TAM`,
          type: "bar",
          data: [{ x: component.key, y: actualTamValue }],
          valueFormatter: unitConversion
        }
      ];
    }).flat();
  
    convertedData.forEach((data) => {
      data.data.forEach((item) => {
        ymax = Math.max(ymax, item.y);
        ymin = Math.min(ymin, item.y);
      });
    });
    maxData = { "yMax": ymax, "yMin": ymin };
    return { convertedData, maxData };
}

export const getAggregationCriteria = (trendType, fieldValue = "", aggregateValue = "") => {
    const defaultData = DefaultSnapshotMonitoringData[trendType];
    const aggregationCriteriaLists : InventoryMonitoringAggregationCriteria = {
        name: defaultData.aggregationCriteriaName,
        typeCriteria: {
            type: {
                operation: AggregationOperation.TERMS,
            },
            field: (fieldValue !== "") ? fieldValue: defaultData.field
        },
        aggregationCriteriaList: [
            {
                name: defaultData.aggregationName,
                typeCriteria: {
                    type: {
                        operation: (aggregateValue === "AVG")? AggregationOperation.AVERAGE : defaultData.operation,
                    },
                    field: defaultData.aggregationField
                }
            },
        ]
    }
    return aggregationCriteriaLists;
}

export const getConfiguredCurrentTamAggregationCriteria = (trendType, fieldValue = "", aggregateValue = "") => {
    const defaultData = DefaultSnapshotMonitoringData[trendType];
    const aggregationCriteriaLists : InventoryMonitoringAggregationCriteria[] = [{
        name: defaultData.aggregationCriteriaName,
        typeCriteria: {
            type: {
                operation: AggregationOperation.TERMS,
            },
            field: (fieldValue !== "") ? fieldValue: defaultData.field
        },
        aggregationCriteriaList: [
            {
                name: defaultData.aggregationName,
                typeCriteria: {
                    type: {
                        operation: (aggregateValue === "AVG")? AggregationOperation.AVERAGE : defaultData.operation,
                    },
                    field: defaultData.aggregationField
                }
            },
            {
                name: defaultData.aggregationName2,
                typeCriteria: {
                    type: {
                        operation: (aggregateValue === "AVG")? AggregationOperation.AVERAGE : defaultData.operation,
                    },
                    field: defaultData.aggregationField2
                }
            }
        ]
    }]
    return aggregationCriteriaLists;
}

export function convertHistoricalTAMDataFormat(response) {
  let ymax = 0;
  let ymin = Infinity;
  const seriesMap = {};

  let responseArray;
  if (Array.isArray(response)) {
    responseArray = response;
  }
  else if (response?.data?.getInventoryMonitoringTrendsVisualisation?.data) {
    responseArray = response.data.getInventoryMonitoringTrendsVisualisation.data;
  }
  else if (response?.getInventoryMonitoringTrendsVisualisation?.data) {
    responseArray = response.getInventoryMonitoringTrendsVisualisation.data;
  }
  else {
    responseArray = [];
  }

  const percentageFormatter = (value) => `${parseFloat(value).toFixed(0)}%`;
  
  responseArray.forEach((trendObj) => {
    
    trendObj.views.forEach(view => {
      let seriesName;
      if (view.name.includes('.')) {
        const parts = view.name.split('.');
        seriesName = parts.length > 1 ? parts[1] : view.name;
      } else {
        seriesName = view.name;
      }
      
      view.values.forEach(val => {
        const timestamp = Number(val.x);
        let xValue;
        if (timestamp === 0 || isNaN(timestamp)) {
          const now = new Date();
          xValue = new Date(now.getFullYear(), now.getMonth(), 1);
        } else {
          xValue = new Date(timestamp);
        }
        const yValue = Number(val.y);
        if (!seriesMap[seriesName]) {
          seriesMap[seriesName] = {
            title: seriesName,
            type: "bar",
            data: [],
            valueFormatter: percentageFormatter
          };
        }
        
        seriesMap[seriesName].data.push({ x: xValue, y: yValue });
        ymax = Math.max(ymax, yValue);
        ymin = Math.min(ymin, yValue);
      });
    });
  });
  Object.values(seriesMap).forEach(series => {
    series.data = series.data.filter(point => point.x instanceof Date && !isNaN(point.x.getTime()));
    if (series.data.length > 0) {
      series.data.sort((a, b) => a.x.getTime() - b.x.getTime());
    }
  });

  const convertedData = Object.values(seriesMap);
  const maxData = { yMax: ymax, yMin: ymin === Infinity ? 0 : ymin };
  return { convertedData, maxData };
}

export const getHistoricalTamAggregationCriteria = (trendType, fieldValue = "") => {
  const defaultData = DefaultSnapshotMonitoringData[trendType];
  const aggregationCriteriaLists : InventoryMonitoringAggregationCriteria[] = [{
      name: defaultData.aggregationCriteriaName,
      typeCriteria: {
          type: {
              operation: AggregationOperation.DATE_HISTOGRAM,
          },
          field: (fieldValue !== "") ? fieldValue: defaultData.field
      },
      aggregationCriteriaList: [{
        name: defaultData.aggregationName,
        typeCriteria: {
          type: {
            operation: AggregationOperation.TERMS
          },
          field: defaultData.aggregationField
        },
        aggregationCriteriaList: [{
          name: defaultData.aggregationName2,
          typeCriteria: {
            type: {
              operation: AggregationOperation.SUM
            },
            field: defaultData.aggregationField2
          }
        }]
      }]
    }]
  return aggregationCriteriaLists;
}

export const historicalTamFormatDate = (date) => {
  if (!(date instanceof Date)) {
    try {
      date = new Date(date);
    } catch (e) {
      return EMPTY;
    }
  }
  return `${date.toLocaleString('en-US', { month: 'short' })}, ${date.getFullYear().toString().slice(-2)}`;
};

export const getFirstAndLastDayOfMonth = (month, year) => {
  const months = [
    "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
  ];
  
  const monthIndex = months.indexOf(month);
  if (monthIndex === -1) {
    const currentDate = new Date();
    return {
      from: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime(),
      to: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getTime()
    };
  }
  
  if (isNaN(year)) {
    year = new Date().getFullYear();
  }

  try {
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);

    return {
      from: firstDay.getTime(),
      to: lastDay.getTime()
    };
  } catch (error) {
    const currentDate = new Date();
    return {
      from: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime(),
      to: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getTime()
    };
  }
};

export const DATE_ENABLE_HOUR = 2;
export const ONE_HOUR_MS = DATE_ENABLE_HOUR*60*60*1000;

export const getTrendsViewDate = (date) => {
  const dt = new Date(date);
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 0, 0, 0)).getTime();
};

export const currentYear = new Date().getFullYear();

export const buildDateRangeQuery = (from, to) => ({
  comparatorQuery: {
    field: BURN_DATE,
    comparator: RANGE as SearchType,
    value: {
      dataType: LONG,
      value: BURN_DATE,
      rangeValue: {
        inclusiveStartValue: from.toString(),
        inclusiveEndValue: to.toString()
      }
    }
  }
});

const monthNames = [
  "January", "February", "March", "April", "May", "June", 
  "July", "August", "September", "October", "November", "December"
];

export const getMonthsBetweenDates = (startTimestamp, endTimestamp) => {
  const startDate = new Date(parseInt(startTimestamp));
  const endDate = new Date(parseInt(endTimestamp));
  
  const months = [];
  
  const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const lastDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  
  while (currentDate <= lastDate) {
    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();
    const monthValue = `${monthNames[monthIndex]}_${year}`;
    
    months.push({
      label: `${monthNames[monthIndex]}, ${year}`,
      value: monthValue
    });
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  return months;
};

export const getDefaultMonths = () => {
  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const months = [];
  
  const lastMonthName = monthNames[lastMonth.getMonth()];
  const lastMonthYear = lastMonth.getFullYear();
  months.push({
    label: `${lastMonthName} ${lastMonthYear}`,
    value: `${lastMonthName}_${lastMonthYear}`
  });
  
  if (today.getMonth() !== lastMonth.getMonth() || today.getFullYear() !== lastMonth.getFullYear()) {
    const currentMonthName = monthNames[today.getMonth()];
    const currentYear = today.getFullYear();
    months.push({
      label: `${currentMonthName} ${currentYear}`,
      value: `${currentMonthName}_${currentYear}`
    });
  }
  
  return months;
};

export const getLastMonthOption = () => {
  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  
  const monthName = monthNames[lastMonth.getMonth()];
  const year = lastMonth.getFullYear();
  
  return {
    label: `${monthName} ${year}`,
    value: `${monthName}_${year}`
  };
};